import React, { useEffect, useRef, useState } from 'react';
import ContentEditable from '../utils/SafeContentEditable';
import { createBlock } from '../utils/blockUtils';
import { focusEditable } from '../utils/focusUtils';
import { applyInlineMarkdown, handleEditablePaste } from '../utils/pasteUtils';
import {
  getCaretSplit,
  isCaretAtStart,
  isCaretAtEnd,
  isCaretOnFirstLine,
  isCaretOnLastLine,
  stripHtml,
} from '../utils/caretUtils';
import { consumeListStartBackspace, handleIndentKeyDown, indentStyle, inheritIndent, shouldHandleLineStartBackspace, startPropFromTyped } from '../utils/indentUtils';
import { useMathPills } from '../hooks/useMathPills';
import { useVariableRefresh } from '../hooks/useVariableRefresh';
import { useVariablePicker } from '../hooks/useVariablePicker';
import { tryDeleteAdjacentAtomic } from '../utils/atomicCaret';

/**
 * BulletBlock — a bulleted list item.
 *
 * Enter chains the next bullet (splitting mid-text carries the remainder
 * into it); Enter on an EMPTY bullet exits the list into a paragraph.
 * Backspace at the start (or when empty) outdents, then drops the bullet,
 * then deletes an empty item. Arrow keys walk into neighbouring blocks.
 */
export const BulletBlock = ({
  block,
  onUpdate,
  onInsertAfter,
  onUpdateAndInsert,
  onNavigate,
  onMergeWithPrevious,
  onMergeWithNext,
  resolveVariable,
  availableVariables,
  onCreateVariable,
}) => {
  const [content, setContent] = useState(block.content || '');
  const contentEditableRef = useRef(null);
  const contentRef = useRef(content);

  // Sync external tree changes (e.g. a merge writing into this block)
  useEffect(() => {
    if ((block.content || '') !== contentRef.current) {
      contentRef.current = block.content || '';
      setContent(block.content || '');
    }
  }, [block.content]);

  // Variable pills show the CURRENT context's values
  useVariableRefresh({
    block,
    contentRef,
    setContent,
    onUpdate,
    resolveVariable,
    contentEditableRef,
  });

  const { tryOpenFromBraceKey, variablePicker } = useVariablePicker({
    blockId: block.id,
    contentEditableRef,
    contentRef,
    setContent,
    onUpdate,
    resolveVariable,
    availableVariables,
    onCreateVariable,
  });

  const { mathMenu } = useMathPills({
    blockId: block.id,
    contentEditableRef,
    contentRef,
    setContent,
    onUpdate,
    resolveVariable,
    availableVariables,
    onCreateVariable,
  });

  const handleChange = (e) => {
    const value = applyInlineMarkdown(e.target.value);
    const typed = stripHtml(value).replace(/\u00a0/g, ' ');
    const prevTyped = stripHtml(contentRef.current).replace(/\u00a0/g, ' ');

    // "[]" or "[ ]" at the start converts this bullet into a checkbox
    if (/^\[\s?\]/.test(typed) && !/^\[\s?\]/.test(prevTyped)) {
      const remainder = value.replace(/^\[(\s|&nbsp;|\u00a0)?\]/, '');
      onUpdate(block.id, {
        type: 'checkbox',
        content: remainder,
        props: { checked: false, ...inheritIndent(block) },
      });
      focusEditable(block.id, { atStart: true });
      return;
    }

    if (/^\d+\. /.test(typed) && !/^\d+\. /.test(prevTyped)) {
      const remainder = value.replace(/^\d+\.(&nbsp;|\u00a0| )/, '');
      onUpdate(block.id, {
        type: 'number',
        content: remainder,
        props: { ...inheritIndent(block), ...startPropFromTyped(typed) },
      });
      focusEditable(block.id, { atStart: true });
      return;
    }

    contentRef.current = value;
    setContent(value);
    onUpdate(block.id, { content: value });
  };

  const handleKeyDown = (e) => {
    const el = contentEditableRef.current;
    const isEmpty = stripHtml(contentRef.current).trim() === '';

    if (handleIndentKeyDown(e, block, onUpdate, el)) return;
    if (tryOpenFromBraceKey(e)) return;

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isEmpty) {
        // Enter on an empty bullet exits the list into a paragraph
        onUpdate(block.id, { type: 'text', content: '', props: {} });
        focusEditable(block.id);
        return;
      }
      const split = el ? getCaretSplit(el) : null;
      if (split && stripHtml(split.after).length > 0) {
        contentRef.current = split.before;
        setContent(split.before);
        onUpdateAndInsert(
          block.id,
          { content: split.before },
          createBlock('bullet', split.after, inheritIndent(block)),
          { focusAtStart: true }
        );
      } else {
        onInsertAfter?.(block.id, createBlock('bullet', '', inheritIndent(block)));
      }
      return;
    }

    if (e.key === 'ArrowUp' && el && isCaretOnFirstLine(el)) {
      if (onNavigate?.(block.id, 'up')) e.preventDefault();
      return;
    }
    if (e.key === 'ArrowDown' && el && isCaretOnLastLine(el)) {
      if (onNavigate?.(block.id, 'down')) e.preventDefault();
      return;
    }
    if (
      e.key === 'ArrowLeft' &&
      !e.shiftKey &&
      !e.altKey &&
      !e.metaKey &&
      !e.ctrlKey &&
      el &&
      isCaretAtStart(el)
    ) {
      if (onNavigate?.(block.id, 'left')) e.preventDefault();
      return;
    }
    if (
      e.key === 'ArrowRight' &&
      !e.shiftKey &&
      !e.altKey &&
      !e.metaKey &&
      !e.ctrlKey &&
      el &&
      isCaretAtEnd(el)
    ) {
      if (onNavigate?.(block.id, 'right')) e.preventDefault();
      return;
    }

    if (
      shouldHandleLineStartBackspace(e, el, { isEmpty, blockId: block.id }) &&
      consumeListStartBackspace(e, block, {
        content: contentRef.current || '',
        onUpdate,
        onMergeWithPrevious,
      })
    ) {
      return;
    }

    if (tryDeleteAdjacentAtomic(e)) {
      if (el) {
        const value = el.innerHTML;
        contentRef.current = value;
        setContent(value);
        onUpdate(block.id, { content: value });
      }
      return;
    }

    // Delete at end: pull the next block's text up into this bullet
    if (e.key === 'Delete' && el && isCaretAtEnd(el)) {
      const live = el.innerHTML;
      contentRef.current = live;
      setContent(live);
      if (onMergeWithNext?.(block.id, live)) {
        e.preventDefault();
        return;
      }
    }
  };

  return (
    <>
      <div className="bullet-block" style={indentStyle(block)}>
        <span className="bullet-marker" aria-hidden="true">•</span>
        <ContentEditable
          innerRef={contentEditableRef}
          html={content}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handleEditablePaste}
          className="bullet-content doc-editable"
          tagName="p"
          placeholder="List item…"
        />
      </div>
      {variablePicker}
      {mathMenu}
    </>
  );
};
